import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

export const useManageDeliveryMethodsStyles = makeStyles({
  promptCell: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  noPromptText: {
    fontStyle: "italic",
    color: designTokens.neutral[500],
  },
  errorAlert: {
    padding: designTokens.spacing[3],
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: designTokens.spacing[4],
  },
  dialogField: {
    marginBottom: designTokens.spacing[4],
  },
  pickerDialogSurface: {
    inlineSize: "min(940px, 90vw)",
    maxInlineSize: "none",
    maxBlockSize: "88vh",
    overflow: "hidden",
    boxSizing: "border-box",
    paddingBottom: 0,
  },
  pickerDialogContent: {
    display: "flex",
    flexDirection: "column",
    blockSize: `calc(88vh - 240px)`,
    overflowY: "hidden",
    paddingTop: designTokens.spacing[4],
    paddingBottom: 0,
    paddingLeft: designTokens.spacing[5],
    paddingRight: designTokens.spacing[5],
  },
  pickerStickyHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: designTokens.neutral.white,
    zIndex: 1,
    paddingBottom: designTokens.spacing[2],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  pickerFilterRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
  },
  pickerSearchBox: {
    flex: 1,
  },
  selectionSummary: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
  },
  selectionLabel: {
    color: designTokens.neutral[600],
    fontSize: designTokens.fontSize.sm,
    whiteSpace: "nowrap",
    fontWeight: designTokens.fontWeight.medium,
  },
  selectionTitle: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: designTokens.fontSize.sm,
  },
  selectionNone: {
    flex: 1,
    color: designTokens.neutral[400],
    fontSize: designTokens.fontSize.sm,
    fontStyle: "italic",
  },
  pickerScrollable: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[4],
  },
  pickerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: designTokens.spacing[4],
    "@media (max-width: 700px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (max-width: 500px)": {
      gridTemplateColumns: "1fr",
    },
  },
  pickerEmptyState: {
    gridColumn: "1 / -1",
    padding: designTokens.spacing[8],
    textAlign: "center",
    color: designTokens.neutral[500],
  },
  pickerLoadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minBlockSize: "200px",
  },
  cardWrapper: {
    minWidth: "0",
    borderRadius: designTokens.borderRadius.lg,
    border: "2px solid transparent",
    cursor: "pointer",
    transition: `border-color ${designTokens.transition.fast}`,
    "& > *": {
      height: "100%",
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  cardWrapperSelected: {
    border: `2px solid ${projectIqTokens.teal}`,
    borderRadius: designTokens.borderRadius["2xl"],
  },
  formDialogSurface: {
    inlineSize: "min(680px, 90vw)",
    maxInlineSize: "none",
    maxBlockSize: "95vh",
    overflow: "hidden",
    boxSizing: "border-box",
    paddingBottom: 0,
  },
  formDialogContent: {
    display: "flex",
    flexDirection: "column",
    blockSize: `calc(95vh - 240px)`,
    overflowY: "auto",
    paddingTop: designTokens.spacing[4],
    paddingBottom: designTokens.spacing[4],
    paddingLeft: designTokens.spacing[5],
    paddingRight: designTokens.spacing[5],
  },
  formLoadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minBlockSize: "300px",
  },
  formFields: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  selectedComponentsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
  },
  selectedComponentsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
  },
  emptySelection: {
    color: designTokens.neutral[400],
    fontSize: designTokens.fontSize.sm,
    fontStyle: "italic",
  },
  tagRemoveButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "inherit",
    marginLeft: designTokens.spacing[1],
    ":hover": {
      opacity: 0.7,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  kcSelectorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  kcGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: designTokens.spacing[2],
  },
  kcButton: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  noComponentsAvailable: {
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.sm,
  },
  documentsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
  },
  documentFileList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    marginTop: designTokens.spacing[1],
  },
  documentFileCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.neutral.white,
  },
  documentFileIconBox: {
    width: "40px",
    height: "40px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: "rgba(47,158,110,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: projectIqTokens.green,
    fontSize: "20px",
  },
  documentFileMeta: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flex: 1,
    minWidth: 0,
  },
  documentFileName: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  documentFileDetails: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
  documentFileCardLoading: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.neutral.white,
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.sm,
  },
});
